// Privacy Policy Page (Dark Theme)
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import GoldenSpiral from '../components/GoldenSpiral';

function Privacy() {
  return (
    <div className="min-h-screen bg-[#0d1117] relative overflow-hidden">
      {/* Gradient background overlay */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl" />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative px-6 py-4 sticky top-0 bg-[#0d1117]/80 backdrop-blur-md z-50 border-b border-white/10">
        <nav className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-600 rounded-xl flex items-center justify-center">
              <GoldenSpiral className="w-6 h-6" color="white" />
            </div>
            <span className="font-bold text-xl tracking-tight text-white">VinciKit</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </nav>
      </header>

      <div className="relative max-w-3xl mx-auto py-12 px-6">
        <h1 className="text-3xl font-bold text-white mb-8 tracking-tight">Privacy Policy</h1>

        <div className="space-y-8">
          <p className="text-gray-500 text-sm">Last updated: December 2025</p>

          <section className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">1. Introduction</h2>
            <p className="text-gray-400 leading-relaxed">
              VinciKit ("we", "our", or "us") respects your privacy and is committed to protecting your personal data.
              This privacy policy explains how we collect, use, and safeguard your information when you use our service.
            </p>
          </section>

          <section className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">2. Information We Collect</h2>
            <p className="text-gray-400 leading-relaxed mb-4">We may collect the following types of information:</p>
            <ul className="list-disc pl-6 text-gray-400 space-y-2">
              <li><strong className="text-gray-300">Account Information:</strong> Email address and authentication data when you sign in</li>
              <li><strong className="text-gray-300">Usage Data:</strong> Information about how you use our service, including project goals and tool preferences</li>
              <li><strong className="text-gray-300">Session Data:</strong> Your AI stack configuration sessions and recommendations</li>
            </ul>
          </section>

          <section className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-400 leading-relaxed mb-4">We use your information to:</p>
            <ul className="list-disc pl-6 text-gray-400 space-y-2">
              <li>Provide and improve our AI stack recommendation service</li>
              <li>Save your session history for future reference</li>
              <li>Communicate with you about our service</li>
              <li>Analyze usage patterns to improve our recommendations</li>
            </ul>
          </section>

          <section className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">4. Data Sharing</h2>
            <p className="text-gray-400 leading-relaxed mb-4">
              We do not sell your personal information. We may share data with:
            </p>
            <ul className="list-disc pl-6 text-gray-400 space-y-2">
              <li><strong className="text-gray-300">AI Service Providers:</strong> We use Claude (Anthropic) and Gemini (Google) to generate recommendations. Your project goals are sent to these services.</li>
              <li><strong className="text-gray-300">Infrastructure Providers:</strong> We use Firebase (Google) for authentication and data storage.</li>
            </ul>
          </section>

          <section className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">5. Data Security</h2>
            <p className="text-gray-400 leading-relaxed">
              We implement appropriate security measures to protect your data, including encryption in transit and at rest.
              However, no method of transmission over the Internet is 100% secure.
            </p>
          </section>

          <section className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">6. Your Rights</h2>
            <p className="text-gray-400 leading-relaxed mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 text-gray-400 space-y-2">
              <li>Access your personal data</li>
              <li>Request deletion of your data</li>
              <li>Opt out of marketing communications</li>
            </ul>
          </section>

          <section className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">7. Cookies</h2>
            <p className="text-gray-400 leading-relaxed">
              We use essential cookies for authentication and session management. We do not use tracking cookies for advertising.
            </p>
          </section>

          <section className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">8. Changes to This Policy</h2>
            <p className="text-gray-400 leading-relaxed">
              We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page.
            </p>
          </section>

          <section className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">9. Contact Us</h2>
            <p className="text-gray-400 leading-relaxed">
              If you have questions about this privacy policy, please contact us at:{' '}
              <a href="mailto:support@vincikit.com" className="text-purple-400 hover:text-purple-300 transition-colors">
                support@vincikit.com
              </a>
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative px-6 py-10 border-t border-white/10 bg-[#0d1117]">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-600 rounded-lg flex items-center justify-center">
              <GoldenSpiral className="w-5 h-5" color="white" />
            </div>
            <span className="font-semibold text-white">VinciKit</span>
          </div>
          <div className="flex items-center gap-8">
            <Link to="/terms" className="text-sm text-gray-500 hover:text-white transition-colors">Terms</Link>
            <Link to="/pricing" className="text-sm text-gray-500 hover:text-white transition-colors">Pricing</Link>
          </div>
          <p className="text-sm text-gray-500">
            © 2025 VinciKit. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default Privacy;
